import { useEffect, useRef } from 'react';
import { api, ApiError } from '../onboarding/api.ts';
import { useCockpit } from './store.ts';

// While a run is running or stop-requested, the cockpit polls the run and its
// new events (§4.9). The backend's `pollAfterMs` sets the interval (default
// 2000 ms) and `null` stops polling. One poll failure keeps the last confirmed
// view, marks it stale, and retries after the backend's Retry-After or 10 s; it
// never fabricates or replays lines. Pausing presentation does not stop polling.

/** `undefined` → default interval, `null` → stop, number → that interval. */
export function pollDelay(pollAfterMs: number | null | undefined): number {
  return pollAfterMs === undefined ? 2000 : (pollAfterMs ?? 0);
}

export function useRunPolling(engagementId: string | undefined) {
  const status = useCockpit((s) => s.runState.status);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    const active = status === 'running' || status === 'stop-requested';
    if (!engagementId || !active) return;

    const poll = (delayMs: number) => {
      if (delayMs <= 0) return;
      timer.current = setTimeout(async () => {
        if (cancelled.current) return;
        try {
          const view = await api.engagementRun(engagementId);
          const after = useCockpit.getState().events.at(-1)?.sequence;
          const page = await api.engagementRunEvents(engagementId, {
            ...(after !== undefined ? { after } : {}),
          });
          if (cancelled.current) return;
          useCockpit.getState().applyRunView(view, page);
          poll(pollDelay(view.pollAfterMs));
        } catch (e) {
          if (cancelled.current) return;
          useCockpit.getState().pollFailed();
          poll(e instanceof ApiError && e.retryAfterSeconds ? e.retryAfterSeconds * 1000 : 10000);
        }
      }, delayMs);
    };

    poll(pollDelay(useCockpit.getState().run?.pollAfterMs));
    return () => {
      cancelled.current = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [engagementId, status]);
}
